import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Roles } from "../../constants/roles";
import { anatomyConstants } from "../../constants/anatomyConstants";

/**
 * Props:
 * - onClose: () => void
 * - onSubmitAssignments: (assignments: Array<{email:string, role:'Viewer'|'Editor'}>) => Promise<
 *      Array<{ email: string, role: string, status: 'member-added'|'already-member'|'not-found'|'self'|'invalid'|'error', message?: string }>
 *   >
 * - closeOnAllOk?: boolean (default: true) -> trigger delayed auto-close when all results OK
 * - autoCloseDelayMs?: number (default: 1000) -> delay before auto-closing
 */
const ShareModal = ({
  onClose,
  onSubmitAssignments,
  closeOnAllOk = true,
  autoCloseDelayMs = 1000,
}) => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const [rawInput, setRawInput] = useState("");
  const [rows, setRows] = useState([]); // [{ email, role, isValid }]
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // array of result objects
  const [error, setError] = useState("");
  const [useEmailList, setUseEmailList] = useState(false);

  const autoCloseTimerRef = useRef(null);

  const allowedRoles = useMemo(
    () =>
      Object.values(Roles).filter(
        (r) => r !== Roles.ADMIN && r !== Roles.CREATOR
      ),
    []
  );

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
  const normalizeEmail = (e) => (e || "").trim().toLowerCase();

  // cleanup any pending timers on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, []);

  // Parse textarea -> unique emails -> rows
  const parseToRows = (text) => {
    const unique = Array.from(
      new Set(
        text
          .split(/[\s,;]+/g) // comma, semicolon, space, newline
          .map(normalizeEmail)
          .filter(Boolean)
      )
    );
    const next = unique.map((email) => ({
      email,
      role: Roles.VIEWER, // default per person
      isValid: emailRegex.test(email),
    }));
    setRows(next);
    setResults(null);
    setError("");
  };

  const handleTextareaChange = (e) => {
    const text = e.target.value;
    setRawInput(text);
    parseToRows(text);
  };

  const handleEmailListToggle = (checked) => {
    setUseEmailList(checked);
    if (
      checked &&
      currentUser?.emailList &&
      Array.isArray(currentUser.emailList) &&
      currentUser.emailList.length > 0
    ) {
      // Add emailList to textarea, merging with existing content
      const emailListText = currentUser.emailList.join("\n");
      const newText = rawInput.trim()
        ? `${rawInput.trim()}\n${emailListText}`
        : emailListText;
      setRawInput(newText);
      parseToRows(newText);
    }
  };

  const removeRow = (email) => {
    const newRows = rows.filter((r) => r.email !== email);
    setRows(newRows);
    // keep textarea in sync (newline separated)
    const newRaw = newRows.map((r) => r.email).join("\n");
    setRawInput(newRaw);
    // clear any previous result for that email
    setResults((prev) =>
      Array.isArray(prev) ? prev.filter((x) => x.email !== email) : prev
    );
  };

  const updateRole = (email, role) => {
    setRows((prev) =>
      prev.map((r) => (r.email === email ? { ...r, role } : r))
    );
  };

  const applyRoleToAll = (role) => {
    if (!role) return;
    setRows((prev) => prev.map((r) => ({ ...r, role })));
  };

  const handleSubmit = async () => {
    setError("");
    setResults(null);

    if (!rows.length) {
      setError("Please enter at least one email.");
      return;
    }

    const invalid = rows.filter((r) => !r.isValid);
    if (invalid.length) {
      setError(`Fix invalid emails before adding. (${invalid.length} invalid)`);
      return;
    }

    setLoading(true);
    try {
      const assignments = rows.map(({ email, role }) => ({ email, role }));
      const res = await onSubmitAssignments(assignments);
      setResults(res || []);

      // schedule delayed auto-close if all OK and user hasn't closed manually
      if (closeOnAllOk && Array.isArray(res) && res.length > 0) {
        const okStatuses = new Set(["member-added", "already-member"]);
        const allOk = res.every((r) => okStatuses.has(r.status));
        if (allOk) {
          if (autoCloseTimerRef.current)
            clearTimeout(autoCloseTimerRef.current);
          autoCloseTimerRef.current = setTimeout(() => {
            autoCloseTimerRef.current = null;
            onClose();
          }, autoCloseDelayMs);
        }
      }
    } catch (err) {
      setError(err?.message || "Could not share right now.");
    } finally {
      setLoading(false);
    }
  };

  const successCount = useMemo(
    () =>
      Array.isArray(results)
        ? results.filter((r) =>
            ["member-added", "already-member"].includes(r.status)
          ).length
        : 0,
    [results]
  );

  const handleManualClose = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      <div className="bg-white p-6 sm:p-8 rounded shadow-lg w-full max-w-2xl mx-auto portrait:mx-4 sm:mx-auto landscape:max-h-[80vh] landscape:overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4 text-[var(--main-color)]">
          Share Case
        </h2>

        {/* Email List Button */}
        {currentUser?.emailList &&
          Array.isArray(currentUser.emailList) &&
          currentUser.emailList.length > 0 && (
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useEmailList}
                  onChange={(e) => handleEmailListToggle(e.target.checked)}
                  className="w-4 h-4 accent-[var(--main-color)] border-gray-300 rounded focus:accent-[var(--main-color-bold)] focus:ring-[var(--main-color-bold)]"
                />
                <span className="text-sm font-medium text-gray-700">
                  Use Email List ({currentUser.emailList.length}{" "}
                  {currentUser.emailList.length === 1 ? "email" : "emails"})
                </span>
              </label>
            </div>
          )}

        {/* INPUT */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Paste email addresses
            </label>
            <textarea
              rows={4}
              value={rawInput}
              onChange={handleTextareaChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-[var(--main-color)] focus:border-[var(--main-color)] sm:text-sm"
              placeholder="Paste emails separated by comma, space, semicolon, or newline."
            />
            <p className="mt-1 text-xs text-gray-500">
              {rows.length} unique {rows.length === 1 ? "email" : "emails"}{" "}
              detected
              {rows.length > 0 && (
                <>
                  {" "}
                  ·{" "}
                  {rows
                    .slice(0, 3)
                    .map((r) => r.email)
                    .join(", ")}
                  {rows.length > 3 ? "…" : ""}
                </>
              )}
            </p>
          </div>

          {/* Quick apply role to all */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              Quick set role for all:
            </span>
            <select
              onChange={(e) => applyRoleToAll(e.target.value)}
              defaultValue=""
              className="border-gray-300 rounded-md shadow-sm focus:ring-[var(--main-color)] focus:border-[var(--main-color)] text-sm"
            >
              <option value="" disabled>
                Choose role
              </option>
              {allowedRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>

          {/* ROWS */}
          <div className="border rounded-md overflow-hidden">
            <div className="grid grid-cols-12 px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">
              <div className="col-span-7">Email</div>
              <div className="col-span-3">Role</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            <div className="divide-y max-h-64 overflow-y-auto">
              {rows.length === 0 ? (
                <div className="px-3 py-3 text-sm text-gray-500">
                  No emails yet.
                </div>
              ) : (
                rows.map((r) => {
                  const res = Array.isArray(results)
                    ? results.find((x) => x.email === r.email)
                    : null;
                  const status = res?.status;
                  const statusColor =
                    status === "member-added"
                      ? "text-green-600"
                      : status === "already-member"
                      ? "text-gray-600"
                      : ["not-found", "invalid", "self", "error"].includes(
                          status
                        )
                      ? "text-red-600"
                      : "text-gray-400";

                  return (
                    <div
                      key={r.email}
                      className="grid grid-cols-12 items-center px-3 py-2"
                    >
                      <div className="col-span-7">
                        <div
                          className={`truncate text-sm ${
                            r.isValid ? "text-gray-800" : "text-red-600"
                          }`}
                          title={r.email}
                        >
                          {r.email}
                        </div>
                        {!r.isValid && (
                          <div className="text-xs text-red-500">
                            Invalid email
                          </div>
                        )}
                        {res?.message && (
                          <div className={`text-xs ${statusColor}`}>
                            {res.message}
                          </div>
                        )}
                      </div>

                      <div className="col-span-3">
                        <select
                          value={r.role}
                          onChange={(e) => updateRole(r.email, e.target.value)}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-[var(--main-color)] focus:border-[var(--main-color)] text-sm"
                        >
                          {allowedRoles.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-span-2 flex items-center justify-end gap-2">
                        {status && (
                          <span className={`text-xs ${statusColor}`}>
                            {status.replace("-", " ")}
                          </span>
                        )}
                        <button
                          onClick={() => removeRow(r.email)}
                          className="text-sm px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                          type="button"
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

        {Array.isArray(results) && results.length > 0 && (
          <div className="mt-3 text-sm text-gray-700">
            Completed: {successCount}/{rows.length} successful
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleManualClose}
            className="bg-gray-500 text-white px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-gray-300 hover:bg-gray-600 transition"
            disabled={loading}
          >
            Close
          </button>
          <button
            onClick={handleSubmit}
            className="bg-[var(--main-color)] text-white px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-[var(--main-color)] hover:bg-[var(--main-color-bold)] transition disabled:opacity-60"
            disabled={
              loading || rows.length === 0 || rows.some((r) => !r.isValid)
            }
          >
            {loading ? "Adding…" : `Add ${rows.length || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
